import {
  createContext,
  useContext,
  useReducer,
  type ReactNode,
  type Dispatch,
} from "react";
import type {
  SiteData,
  BracketData,
  CustomGameSettings,
  GameMode,
  GameType,
  BracketSize,
  SongData,
} from "../types";
import { setupSeeds } from "../engine/seeds";
import { pickWinner as enginePickWinner } from "../engine/gameModes";

// ── State ──
export interface GameState {
  siteData: SiteData;
  brackets: BracketData;
}

const defaultCustomSettings: CustomGameSettings = {
  Genres: true,
  GenreSelected: "Rap",
  Views: false,
  ViewCount: 0,
  StrictlyLyrics: false,
  AsAnArtist: false,
  MinProjects: 2,
  Artists: true,
  Songs: false,
  yearActiveMin: 0,
  yearActiveMax: 0,
};

export const initialSiteData: SiteData = {
  gameMode: "",
  gameType: "SingleE",
  bracketType: "Winners",
  artistSelected: "",
  bracketSize: -1,
  gameFinished: false,
  collabProjects: false,
  gameSessionSongs: [],
  customGameSettings: { ...defaultCustomSettings },
  bracketsModeSettings: {
    current2Seeds: [],
    currentSeed: -1,
    bracketLevel: 1,
    currentRound: 1,
    maxSeeds: -1,
    gameResults: [],
    gameResultSeeds: [],
    gameSeedSetup: {
      seedList: [],
      activeSeeds: {},
      nextSeedList: [],
      nextSeeds: {},
      losersSeedList: [],
      losersSeeds: {},
    },
    matchupHistory: [],
  },
};

const initialState: GameState = {
  siteData: initialSiteData,
  brackets: { winners: [], losers: [] },
};

// ── Actions ──
export type GameAction =
  | {
      type: "START_GAME";
      payload: {
        gameMode: GameMode;
        gameType: GameType;
        bracketSize: BracketSize;
        songs: SongData[];
        artistSelected?: string;
        collabProjects?: boolean;
        customSettings?: CustomGameSettings;
      };
    }
  | { type: "PICK_WINNER"; payload: { winner: number } }
  | { type: "RESET_GAME" }
  | { type: "CONTINUE_GAME"; payload: { siteData: SiteData; brackets: BracketData } }
  | { type: "SET_CUSTOM_SETTINGS"; payload: Partial<CustomGameSettings> };

// ── Reducer ──
function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case "START_GAME": {
      const { gameMode, gameType, bracketSize, songs, artistSelected, collabProjects, customSettings } =
        action.payload;
      let siteData: SiteData = {
        ...initialSiteData,
        gameMode,
        gameType,
        bracketSize,
        gameSessionSongs: songs,
        artistSelected: artistSelected ?? "",
        collabProjects: collabProjects ?? false,
        customGameSettings: customSettings ?? { ...defaultCustomSettings },
        bracketsModeSettings: {
          ...initialSiteData.bracketsModeSettings,
          currentSeed: 1,
          gameSeedSetup: {
            seedList: [],
            activeSeeds: {},
            nextSeedList: [],
            nextSeeds: {},
            losersSeedList: [],
            losersSeeds: {},
          },
        },
      };
      siteData = setupSeeds(siteData);
      return {
        siteData,
        brackets: { winners: [], losers: [] },
      };
    }

    case "PICK_WINNER": {
      const { current2Seeds, currentRound } = state.siteData.bracketsModeSettings;
      const winner = action.payload.winner;
      const matchupRecord = {
        round: currentRound,
        seed1: current2Seeds[0]!,
        seed2: current2Seeds[1]!,
        winner,
        bracketType: state.siteData.bracketType,
      };
      const result = enginePickWinner(
        state.siteData,
        state.brackets,
        winner,
      );
      // Append matchup to history
      result.siteData.bracketsModeSettings.matchupHistory = [
        ...(state.siteData.bracketsModeSettings.matchupHistory || []),
        matchupRecord,
      ];
      // Save to localStorage
      localStorage.setItem("currentGame", JSON.stringify(result.siteData));
      localStorage.setItem(
        `brackets${result.siteData.bracketSize}`,
        JSON.stringify(result.brackets),
      );
      return {
        siteData: result.siteData,
        brackets: result.brackets,
      };
    }

    case "RESET_GAME":
      localStorage.removeItem("currentGame");
      localStorage.removeItem("brackets8");
      localStorage.removeItem("brackets16");
      localStorage.removeItem("brackets32");
      localStorage.removeItem("brackets64");
      return { ...initialState };

    case "CONTINUE_GAME": {
      const continued = action.payload.siteData;
      if (!continued.bracketsModeSettings.matchupHistory) {
        continued.bracketsModeSettings.matchupHistory = [];
      }
      return {
        siteData: continued,
        brackets: action.payload.brackets,
      };
    }

    case "SET_CUSTOM_SETTINGS":
      return {
        ...state,
        siteData: {
          ...state.siteData,
          customGameSettings: {
            ...state.siteData.customGameSettings,
            ...action.payload,
          },
        },
      };

    default:
      return state;
  }
}

// ── Context ──
const GameContext = createContext<GameState>(initialState);
const GameDispatchContext = createContext<Dispatch<GameAction>>(() => {});

export function GameProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  return (
    <GameContext.Provider value={state}>
      <GameDispatchContext.Provider value={dispatch}>
        {children}
      </GameDispatchContext.Provider>
    </GameContext.Provider>
  );
}

export function useGame() {
  return useContext(GameContext);
}

export function useGameDispatch() {
  return useContext(GameDispatchContext);
}
